import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import DataTable from 'react-data-table-component';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import * as XLSX from 'xlsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../hooks/useAuth';
import SalesView from './SalesView';
import SearchableSelect from './SearchableSelect';
import { patientCardPath } from '../utils/patientCardLink';
import { formatSalesStatus, salesStatusClass } from '../utils/salesStatus';
import '../styles/SalesTransactions.css';
import '../styles/SalesView.css';

const SALES_STATUS_OPTIONS = [
  { value: 'paid', label: 'Paid' },
  { value: 'partially_paid', label: 'Partially Paid' },
  { value: 'sent', label: 'Sent' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'draft', label: 'Draft' },
  { value: 'void', label: 'Void' },
];

const datePickerSharedProps = {
  dateFormat: 'yyyy-MM-dd',
  className: 'datepicker',
  withPortal: true,
  popperClassName: 'sales-adv-datepicker-popper',
  calendarClassName: 'sales-adv-datepicker-calendar',
  showMonthDropdown: true,
  showYearDropdown: true,
  dropdownMode: 'select',
  scrollableYearDropdown: true,
  yearDropdownItemNumber: 20,
  fixedHeight: true,
};

const salesListUrl = () =>
  `${process.env.REACT_APP_SERVER_BASE_URL}${process.env.REACT_APP_API_SALES_TRANSACTIONS}/list`;

const getBranchFromNumber = (number) => {
  if (!number) return '';
  return String(number).startsWith('K-') ? 'Kissy' : 'Pultney';
};

const formatMoney = (value) =>
  parseFloat(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const toIsoDate = (date) => {
  if (!date) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

const NO_DOCTOR = 'No Doctor';
const ADDITIONAL_FILTERS_KEY = '/sales-transactions/additional-filters';

const SalesTransactions = () => {
  const { hasPermission } = useAuth();
  const canAdd = hasPermission('/sales-transactions', 'Add');
  const canExport = hasPermission('/sales-transactions', 'Export');
  const canUseAdditionalFilters = hasPermission(ADDITIONAL_FILTERS_KEY, 'View');

  const [doctors, setDoctors] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState('');
  const [startDate, setStartDate] = useState(() => startOfToday());
  const [endDate, setEndDate] = useState(() => startOfToday());
  const [salesNumber, setSalesNumber] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [patientInfo, setPatientInfo] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [receipts, setReceipts] = useState([]);
  const [searchParams, setSearchParams] = useState(() => {
    const today = toIsoDate(new Date());
    return {
      startDate: today,
      endDate: today,
      salesNumber: '',
      doctorName: '',
      customerName: '',
      patientInfo: '',
      salesType: '',
      branch: '',
    };
  });
  const [viewMode, setViewMode] = useState('list'); // list | view | create
  const [initialSale, setInitialSale] = useState(null);
  const [createSalesType, setCreateSalesType] = useState(null);
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [showTypePicker, setShowTypePicker] = useState(false);
  const [isSalesEditing, setIsSalesEditing] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [selectedSalesType, setSelectedSalesType] = useState(''); // '' | Invoice | Sales Receipt
  const [selectedBranch, setSelectedBranch] = useState(''); // '' | Pultney | Kissy
  const [selectedStatuses, setSelectedStatuses] = useState([]); // status keys

  const [advDoctor, setAdvDoctor] = useState('');
  const [advCustomer, setAdvCustomer] = useState('');
  const [advPatient, setAdvPatient] = useState('');
  const [advStartDate, setAdvStartDate] = useState(() => startOfToday());
  const [advEndDate, setAdvEndDate] = useState(() => startOfToday());
  const [advSalesType, setAdvSalesType] = useState('');
  const [advBranch, setAdvBranch] = useState('');
  const [advStatuses, setAdvStatuses] = useState([]);
  const [advSearchError, setAdvSearchError] = useState('');

  useEffect(() => {
    if (canUseAdditionalFilters) {
      axios
        .get(
          `${process.env.REACT_APP_SERVER_BASE_URL}${process.env.REACT_APP_API_GET_DOCTORS}`
        )
        .then((response) => {
          setDoctors(response.data.doctors || []);
        })
        .catch((error) => {
          console.error('Error fetching doctors:', error);
        });
    }

    axios
      .get(`${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/customers`, {
        params: { status: 'Active' },
      })
      .then((response) => {
        setCustomers(response.data || []);
      })
      .catch((error) => {
        console.error('Error fetching customers:', error);
      });
  }, [canUseAdditionalFilters]);

  const fetchReceipts = useCallback(async (params) => {
    try {
      setIsLoading(true);
      setSearchError('');
      const response = await axios.get(salesListUrl(), { params });
      setReceipts(response.data || []);
    } catch (error) {
      console.error('Error fetching receipts:', error);
      setReceipts([]);
      setSearchError(
        error.response?.data?.error || 'Could not load sales transactions.'
      );
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (searchParams) {
      fetchReceipts(searchParams);
    }
  }, [searchParams, fetchReceipts]);

  const buildSearchParams = (overrides = {}) => {
    const number =
      overrides.salesNumber !== undefined ? overrides.salesNumber : salesNumber;
    const hasNumber = Boolean(String(number || '').trim());
    const params = {
      salesNumber: number,
      doctorName: selectedDoctor,
      customerName,
      patientInfo,
      salesType: selectedSalesType,
      branch: selectedBranch,
      salesStatus: selectedStatuses.length ? selectedStatuses.join(',') : '',
      ...overrides,
    };
    // When searching by sales number, ignore date range.
    if (!hasNumber) {
      const start =
        overrides.startDate !== undefined
          ? overrides.startDate
          : toIsoDate(startDate);
      const end =
        overrides.endDate !== undefined ? overrides.endDate : toIsoDate(endDate);
      if (start) params.startDate = start;
      if (end) params.endDate = end;
    } else {
      delete params.startDate;
      delete params.endDate;
    }
    if (!params.salesType) delete params.salesType;
    if (!params.branch) delete params.branch;
    if (!params.salesStatus) delete params.salesStatus;
    if (!canUseAdditionalFilters) params.doctorName = '';
    return params;
  };

  const handleSearch = () => {
    const params = buildSearchParams();
    const hasNumber = Boolean(String(params.salesNumber || '').trim());
    const hasDates = Boolean(params.startDate && params.endDate);
    if (!hasNumber && !hasDates) {
      setSearchError(
        'Date range is required unless you search by Invoice/Receipt Number.'
      );
      return;
    }
    setSearchError('');
    setSearchParams(params);
  };

  const confirmLeaveEdit = () => {
    if (!isSalesEditing) return true;
    return window.confirm(
      'You have unsaved changes. Leave this record without saving?'
    );
  };

  const handleView = (sale) => {
    if (!confirmLeaveEdit()) return;
    setCreateSalesType(null);
    setInitialSale(sale);
    setViewMode('view');
    setIsSalesEditing(false);
  };

  const handleClose = () => {
    if (!confirmLeaveEdit()) return;
    setViewMode('list');
    setInitialSale(null);
    setCreateSalesType(null);
    setIsSalesEditing(false);
  };

  const handleRowSelect = (sale) => {
    if (!sale) return;
    if (
      initialSale?.sales_id != null &&
      String(initialSale.sales_id) === String(sale.sales_id) &&
      viewMode === 'view'
    ) {
      return;
    }
    if (!confirmLeaveEdit()) return;
    setCreateSalesType(null);
    setInitialSale(sale);
    setViewMode('view');
    setIsSalesEditing(false);
  };

  const refreshListAndSelection = async (detail) => {
    const header = detail?.header || detail;
    if (searchParams) {
      await fetchReceipts(searchParams);
    }
    // Sync refreshes SalesView in place; updating selection remounts/reloads and
    // drops Zoho amount_applied from multi-invoice payments.
    if (detail?._syncOnly) {
      return;
    }
    if (header?.sales_id) {
      setInitialSale(header);
      setViewMode('view');
      setCreateSalesType(null);
      setIsSalesEditing(false);
    }
  };

  const openAdvancedSearch = () => {
    setAdvDoctor(canUseAdditionalFilters ? selectedDoctor : '');
    setAdvCustomer(customerName);
    setAdvPatient(patientInfo);
    setAdvStartDate(startDate);
    setAdvEndDate(endDate);
    setAdvSalesType(selectedSalesType);
    setAdvBranch(selectedBranch);
    setAdvStatuses([...selectedStatuses]);
    setAdvSearchError('');
    setShowAdvancedSearch(true);
  };

  const toggleAdvStatus = (value) => {
    setAdvStatuses((prev) =>
      prev.includes(value) ? prev.filter((s) => s !== value) : [...prev, value]
    );
  };

  const applyAdvancedSearch = () => {
    const startIso = toIsoDate(advStartDate);
    const endIso = toIsoDate(advEndDate);
    const params = buildSearchParams({
      doctorName: advDoctor,
      customerName: advCustomer,
      patientInfo: advPatient,
      salesType: advSalesType,
      branch: advBranch,
      salesStatus: advStatuses.length ? advStatuses.join(',') : '',
      startDate: startIso,
      endDate: endIso,
    });
    const hasNumber = Boolean(String(params.salesNumber || '').trim());
    const hasDates = Boolean(params.startDate && params.endDate);
    if (!hasNumber && !hasDates) {
      setAdvSearchError(
        'Date range is required unless you search by Invoice/Receipt Number.'
      );
      return;
    }

    setSelectedDoctor(canUseAdditionalFilters ? advDoctor : '');
    setCustomerName(advCustomer);
    setPatientInfo(advPatient);
    setStartDate(advStartDate);
    setEndDate(advEndDate);
    setSelectedSalesType(advSalesType);
    setSelectedBranch(advBranch);
    setSelectedStatuses([...advStatuses]);
    setAdvSearchError('');
    setSearchError('');
    setShowAdvancedSearch(false);
    setSearchParams(params);
  };

  const clearAdvancedSearch = () => {
    const today = startOfToday();
    setAdvDoctor('');
    setAdvCustomer('');
    setAdvPatient('');
    setAdvSalesType('');
    setAdvBranch('');
    setAdvStatuses([]);
    setAdvStartDate(today);
    setAdvEndDate(today);
    setAdvSearchError('');
  };

  const openAdd = () => {
    if (!confirmLeaveEdit()) return;
    setShowTypePicker(true);
  };

  const startCreate = (salesType) => {
    setShowTypePicker(false);
    setCreateSalesType(salesType);
    setInitialSale(null);
    setViewMode('create');
    setIsSalesEditing(true);
  };

  const handleCreated = (detail) => {
    refreshListAndSelection(detail);
  };

  const handleSaved = (detail) => {
    refreshListAndSelection(detail);
  };

  const handleExcelExport = () => {
    if (!receipts.length) return;
    const exportData = receipts.map((row) => ({
      'Sales Date': row.sales_date,
      Branch: getBranchFromNumber(row.sales_number),
      'Sales Number': row.sales_number,
      'Customer Name': row.customer_name,
      'Patient ID': row.patient_id,
      'Patient Name': row.patient_name,
      'Sales Amount': row.sales_total,
      Type: row.sales_type,
      Status: formatSalesStatus(row.sales_status),
    }));
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sales Transactions');
    XLSX.writeFile(workbook, 'sales_transactions.xlsx');
  };

  const todayIso = toIsoDate(new Date());
  const datesDifferFromToday =
    Boolean(startDate && endDate) &&
    (toIsoDate(startDate) !== todayIso || toIsoDate(endDate) !== todayIso);
  const hasAdvancedFilters = Boolean(
    selectedDoctor ||
      customerName ||
      patientInfo ||
      selectedSalesType ||
      selectedBranch ||
      selectedStatuses.length ||
      datesDifferFromToday
  );

  const selectedStatusLabels = selectedStatuses
    .map((value) => SALES_STATUS_OPTIONS.find((o) => o.value === value)?.label || value)
    .join(', ');

  const doctorOptions = [
    { value: NO_DOCTOR, label: NO_DOCTOR },
    ...doctors.map((doc) => ({
      value: doc.name || doc.doctor_name,
      label: doc.name || doc.doctor_name,
    })),
  ];

  const customerOptions = customers.map((customer) => ({
    value: customer.customer_name,
    label: customer.customer_name,
  }));

  const columns = [
    {
      name: 'Sales Number',
      cell: (row) => (
        <button
          type="button"
          className="sales-number-link"
          onClick={(e) => {
            e.stopPropagation();
            handleView(row);
          }}
        >
          {row.sales_number}
        </button>
      ),
      sortable: true,
      sortFunction: (a, b) =>
        String(a.sales_number || '').localeCompare(String(b.sales_number || '')),
    },
    {
      name: 'Sales Date',
      selector: (row) => row.sales_date,
      sortable: true,
    },
    {
      name: 'Branch',
      selector: (row) => getBranchFromNumber(row.sales_number),
      sortable: true,
    },
    {
      name: 'Customer Name',
      selector: (row) => row.customer_name,
      sortable: true,
    },
    {
      name: 'Patient ID',
      selector: (row) => row.patient_id,
      sortable: true,
      cell: (row) => {
        const path = patientCardPath(row.patient_id);
        if (!path) return row.patient_id || '';
        return (
          <Link
            className="sales-patient-link"
            to={path}
            onClick={(e) => e.stopPropagation()}
          >
            {row.patient_id}
          </Link>
        );
      },
    },
    {
      name: 'Patient Name',
      selector: (row) => row.patient_name,
      sortable: true,
    },
    {
      name: 'Status',
      selector: (row) => formatSalesStatus(row.sales_status),
      sortable: true,
      cell: (row) => {
        const label = formatSalesStatus(row.sales_status);
        if (!label) return '';
        return (
          <span
            className={`sales-status-badge sales-status-badge--${salesStatusClass(row.sales_status)}`}
          >
            {label}
          </span>
        );
      },
    },
    {
      name: 'Sales Amount',
      selector: (row) => row.sales_total,
      sortable: true,
      cell: (row) => formatMoney(row.sales_total),
      right: true,
    },
  ];

  const miniColumns = [
    { name: 'Sales Number', selector: (row) => row.sales_number, sortable: true },
    { name: 'Date', selector: (row) => row.sales_date, sortable: true },
    {
      name: 'Status',
      selector: (row) => formatSalesStatus(row.sales_status),
      sortable: true,
      cell: (row) => {
        const label = formatSalesStatus(row.sales_status);
        if (!label) return '';
        return (
          <span
            className={`sales-status-badge sales-status-badge--${salesStatusClass(row.sales_status)}`}
          >
            {label}
          </span>
        );
      },
    },
    {
      name: 'Total',
      selector: (row) => row.sales_total,
      sortable: true,
      cell: (row) => formatMoney(row.sales_total),
    },
  ];

  const conditionalRowStyles = [
    {
      when: (row) =>
        viewMode === 'view' &&
        initialSale &&
        String(row.sales_id) === String(initialSale.sales_id),
      style: {
        backgroundColor: '#dbeafe',
        fontWeight: 600,
      },
    },
  ];

  return (
    <div className="sales-transactions">
      {viewMode === 'list' ? (
        <>
          <div className="sales-transactions-header">
            <div className="filter-group small">
              <label htmlFor="sales-number">Invoice/Receipt Number:</label>
              <input
                type="text"
                id="sales-number"
                value={salesNumber}
                onChange={(e) => setSalesNumber(e.target.value)}
                className="input-field"
              />
            </div>
            <div className="filter-group filter-actions">
              <button type="button" onClick={handleSearch} disabled={isLoading}>
                {isLoading ? 'Searching…' : 'Search'}
              </button>
              <button
                type="button"
                className="secondary"
                onClick={openAdvancedSearch}
              >
                Advanced Search{hasAdvancedFilters ? ' •' : ''}
              </button>
            </div>
          </div>

          {searchError && <div className="sales-search-error">{searchError}</div>}

          {hasAdvancedFilters && (
            <div className="advanced-filters-active">
              Advanced filters active
              {selectedSalesType ? ` · Type: ${selectedSalesType}` : ''}
              {selectedBranch ? ` · Branch: ${selectedBranch}` : ''}
              {selectedStatusLabels ? ` · Status: ${selectedStatusLabels}` : ''}
              {datesDifferFromToday && startDate && endDate
                ? ` · Dates: ${toIsoDate(startDate)} to ${toIsoDate(endDate)}`
                : ''}
              {selectedDoctor ? ` · Doctor: ${selectedDoctor}` : ''}
              {customerName ? ` · Customer: ${customerName}` : ''}
              {patientInfo ? ` · Patient: ${patientInfo}` : ''}
            </div>
          )}

          <div className="sales-transactions-detail">
            <DataTable
              title="Sales Transactions"
              columns={columns}
              data={receipts}
              progressPending={isLoading}
              pagination
              paginationPerPage={50}
              highlightOnHover
              pointerOnHover
              onRowClicked={handleView}
              subHeader
              subHeaderComponent={
                <div className="sales-table-subheader">
                  {canAdd && (
                    <button
                      type="button"
                      className="add-button"
                      onClick={openAdd}
                      title="Add"
                      aria-label="Add sales record"
                    >
                      <FontAwesomeIcon icon={faPlus} />
                    </button>
                  )}
                  {canExport && (
                    <button
                      type="button"
                      className="export-excel-btn"
                      onClick={handleExcelExport}
                      disabled={!receipts.length}
                    >
                      Export to Excel
                    </button>
                  )}
                  {receipts.length >= 1000 && (
                    <span className="sales-limit-note">
                      Showing first 1000 rows. Narrow your search for more.
                    </span>
                  )}
                </div>
              }
            />
          </div>
        </>
      ) : (
        <div className="transactions-and-form">
          <div className="transactions-list">
            <div className="transactions-list-header">
              <h2>Transactions</h2>
              {canAdd && (
                <button
                  type="button"
                  className="add-button"
                  onClick={openAdd}
                  title="Add"
                  aria-label="Add sales record"
                >
                  <FontAwesomeIcon icon={faPlus} />
                </button>
              )}
            </div>
            <DataTable
              columns={miniColumns}
              data={receipts}
              pagination
              paginationPerPage={10}
              highlightOnHover
              pointerOnHover
              onRowClicked={handleRowSelect}
              conditionalRowStyles={conditionalRowStyles}
            />
          </div>
          <div className="sales-form">
            <button type="button" className="close-button" onClick={handleClose} aria-label="Close">
              ×
            </button>
            {viewMode === 'create' && createSalesType ? (
              <SalesView
                key={`new-${createSalesType}`}
                mode="create"
                initialSalesType={createSalesType}
                onCreated={handleCreated}
                onEditingChange={setIsSalesEditing}
              />
            ) : (
              initialSale && (
                <SalesView
                  key={initialSale.zoho_id || initialSale.sales_id}
                  salesData={initialSale}
                  onSaved={handleSaved}
                  onEditingChange={setIsSalesEditing}
                />
              )
            )}
          </div>
        </div>
      )}

      {showAdvancedSearch && (
        <div className="modal-overlay" role="dialog" aria-modal="true">
          <div className="modal-content sales-advanced-search">
            <div className="modal-header-row">
              <h2>Advanced Search</h2>
              <button
                type="button"
                className="modal-close"
                onClick={() => setShowAdvancedSearch(false)}
              >
                ×
              </button>
            </div>

            <div className="adv-field">
              <span className="adv-label">Type</span>
              <div className="adv-type-toggle" role="group" aria-label="Sales type">
                <button
                  type="button"
                  className={!advSalesType ? 'active' : ''}
                  onClick={() => setAdvSalesType('')}
                >
                  All
                </button>
                <button
                  type="button"
                  className={advSalesType === 'Invoice' ? 'active' : ''}
                  onClick={() => setAdvSalesType('Invoice')}
                >
                  Invoice
                </button>
                <button
                  type="button"
                  className={advSalesType === 'Sales Receipt' ? 'active' : ''}
                  onClick={() => setAdvSalesType('Sales Receipt')}
                >
                  Sales Receipt
                </button>
              </div>
            </div>

            <div className="adv-field">
              <span className="adv-label">Branch</span>
              <div className="adv-type-toggle" role="group" aria-label="Branch">
                <button
                  type="button"
                  className={!advBranch ? 'active' : ''}
                  onClick={() => setAdvBranch('')}
                >
                  All
                </button>
                <button
                  type="button"
                  className={advBranch === 'Pultney' ? 'active' : ''}
                  onClick={() => setAdvBranch('Pultney')}
                >
                  Pultney
                </button>
                <button
                  type="button"
                  className={advBranch === 'Kissy' ? 'active' : ''}
                  onClick={() => setAdvBranch('Kissy')}
                >
                  Kissy
                </button>
              </div>
            </div>

            <div className="adv-field adv-date-range">
              <span className="adv-label">Date Range</span>
              <div className="adv-date-inputs">
                <DatePicker
                  selected={advStartDate}
                  onChange={(date) => setAdvStartDate(date)}
                  placeholderText="Start"
                  {...datePickerSharedProps}
                />
                <span className="adv-date-sep">–</span>
                <DatePicker
                  selected={advEndDate}
                  onChange={(date) => setAdvEndDate(date)}
                  placeholderText="End"
                  {...datePickerSharedProps}
                />
              </div>
            </div>

            <div className="adv-field">
              <span className="adv-label">Status</span>
              <div className="adv-status-multi" role="group" aria-label="Sales status">
                {SALES_STATUS_OPTIONS.map((opt) => (
                  <label key={opt.value} className="adv-status-option">
                    <input
                      type="checkbox"
                      checked={advStatuses.includes(opt.value)}
                      onChange={() => toggleAdvStatus(opt.value)}
                    />
                    <span>{opt.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {canUseAdditionalFilters && (
              <div className="adv-field">
                <SearchableSelect
                  label="Doctor"
                  value={advDoctor}
                  options={doctorOptions}
                  placeholder="Select doctor…"
                  onChange={(opt) => setAdvDoctor(opt.value)}
                />
              </div>
            )}

            <div className="adv-field">
              <SearchableSelect
                label="Customer"
                value={advCustomer}
                options={customerOptions}
                placeholder="Select customer…"
                onChange={(opt) => setAdvCustomer(opt.value)}
              />
            </div>

            <div className="adv-field">
              <label htmlFor="adv-patient" className="sv-select-label">
                Patient Info
              </label>
              <div className="sv-select-control adv-patient-control">
                <input
                  type="text"
                  id="adv-patient"
                  className="sv-select-input"
                  value={advPatient}
                  onChange={(e) => setAdvPatient(e.target.value)}
                  placeholder="Patient ID or name"
                />
              </div>
            </div>

            {advSearchError && (
              <div className="sales-search-error">{advSearchError}</div>
            )}

            <div className="modal-actions">
              <button type="button" className="secondary" onClick={clearAdvancedSearch}>
                Clear
              </button>
              <button type="button" onClick={applyAdvancedSearch}>
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      {showTypePicker && (
        <div className="modal-overlay" role="dialog" aria-modal="true">
          <div className="modal-content sales-type-picker">
            <div className="modal-header-row">
              <h2>Add Sales Record</h2>
              <button
                type="button"
                className="modal-close"
                onClick={() => setShowTypePicker(false)}
              >
                ×
              </button>
            </div>
            <p>What type of record are you creating?</p>
            <div className="modal-actions type-choices">
              <button type="button" onClick={() => startCreate('Invoice')}>
                Invoice
              </button>
              <button type="button" onClick={() => startCreate('Sales Receipt')}>
                Sales Receipt
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesTransactions;
